<script setup lang="ts">
import { useI18n } from '@/composable/useI18n'
import type { IAlarm } from '@/interfaces/entities/alarm'
import { useAlarmStore } from '@/store/alarm'
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const router = useRouter()
const alarmStore = useAlarmStore()
const { listOpenAlarms, openAlarmsLoading, openAlarmsError } = storeToRefs(alarmStore)

const hasFetchedInitially = ref(false)

const openAlarms = computed(() => listOpenAlarms.value ?? [])
const activeCount = computed(() => openAlarms.value.length)

function formatStartTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return format(new Date(dateStr), 'HH:mm:ss')
}

function getSensorLabel(alarm: IAlarm): string {
  return alarm.sensor?.name || alarm.sensor_name || alarm.sensor?.subid || '—'
}

function goToAlarmDetail(alarm: IAlarm) {
  router.push({
    name: 'alarm-detail',
    params: {
      sensorId: String(alarm.sensor?.id ?? 0),
      alarmId: String(alarm.id),
    },
  })
}

onMounted(async () => {
  await alarmStore.fetchOpenAlarms()
  hasFetchedInitially.value = true
})
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div
      v-if="openAlarmsLoading && !hasFetchedInitially"
      class="flex flex-1 items-center justify-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.open_alarms.loading') }}
    </div>

    <div
      v-else-if="openAlarmsError && openAlarms.length === 0"
      class="flex flex-1 items-center justify-center px-4 text-center text-sm text-red"
    >
      {{ t('custom_dashboards.view.open_alarms.error') }}
    </div>

    <div
      v-else-if="openAlarms.length === 0"
      class="flex flex-1 items-center justify-center px-4 text-center text-sm text-body"
    >
      {{ t('custom_dashboards.view.open_alarms.empty') }}
    </div>

    <template v-else>
      <div class="min-h-0 flex-1 overflow-y-auto px-3 pb-2 pt-2">
        <ul class="flex flex-col gap-2">
          <li
            v-for="alarm in openAlarms"
            :key="alarm.id"
            class="flex items-center gap-3 border-l-4 border-l-red-600 bg-red-50 px-3 py-3"
          >
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-bold text-black">
                {{ alarm.name }}
              </p>
              <p class="truncate text-2xs font-medium uppercase tracking-widest text-red-600">
                {{ getSensorLabel(alarm) }}
              </p>
            </div>

            <div class="flex shrink-0 items-center gap-3">
              <span class="font-mono text-xs text-gray-600">
                {{ formatStartTime(alarm.start_date) }}
              </span>
              <button
                type="button"
                class="border border-red-600 bg-red-50 px-2.5 py-1 text-2xs font-bold uppercase tracking-wide text-red-600 hover:bg-red-600 hover:text-white"
                @click="goToAlarmDetail(alarm)"
              >
                {{ t('custom_dashboards.view.open_alarms.view_details') }}
              </button>
            </div>
          </li>
        </ul>
      </div>

      <div class="shrink-0 border-t border-stroke px-4 py-2">
        <p class="text-right text-2xs font-bold uppercase tracking-widest text-red-600">
          {{ t('custom_dashboards.view.open_alarms.active_count', { count: activeCount }) }}
        </p>
      </div>
    </template>
  </div>
</template>
